import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import SelectableCard from "./SelectableCard";
import AddExamModal from "./AddExamModal";
import Exam2PlanStep from "./Exam2PlanStep";
import CustomizeExamModal from "./CustomizeExamModal";
import { resolveExam2NoteType } from "./exam2LayoutPrefs";

/**
 * Exam step: Exam (keep/hide) or Exam 2 (systems table + differential tips).
 * Shared across Events, Admission H&P, Progress, and outpatient.
 * plan2Only (Admission/Progress): systems table only, labeled "Exam" (no toggle).
 * Exam2: Customize exam… (Option 2) edits this-note layout or My defaults.
 */
const ExamPlanStep = ({
  examFindings = [],
  loading = false,
  error = "",
  retry,
  disabledExamIds = new Set(),
  onToggleExamDisabled,
  onAddExam,
  examMode = "exam2",
  onExamModeChange,
  exam2Systems = [],
  onChangeExam2SystemValue,
  diagnoses = [],
  patientContext = null,
  preferredBySystemId = {},
  onMakeDefaultNormal,
  onCreateNormalExam,
  onReplaceExam2Systems,
  onSaveExam2LayoutDefaults,
  exam2LayoutScope = null,
  /** Page-level Clinical Hints open state (Management). */
  clinicalHintsOpen,
  /** Admission/Progress: lock to systems table; hide Exam | Exam 2 toggle. */
  plan2Only = false,
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const existingNames = examFindings.map((finding) => finding.name);
  const mode = plan2Only || examMode === "exam2" ? "exam2" : "exam";

  const scopeLabel = useMemo(() => {
    if (!exam2LayoutScope) return "";
    const specialty = String(exam2LayoutScope.specialty || "").trim() || "General";
    const setting = String(exam2LayoutScope.setting || "").trim() || "outpatient";
    const noteType = resolveExam2NoteType(exam2LayoutScope);
    return `${specialty} · ${setting} · ${noteType}`;
  }, [exam2LayoutScope]);

  return (
    <Box sx={{ width: "100%", mb: 4 }}>
      <Box sx={{ textAlign: "left", mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="h5">Exam</Typography>
          {mode === "exam2" && (onReplaceExam2Systems || onSaveExam2LayoutDefaults) ? (
            <Button size="small" onClick={() => setCustomizeOpen(true)}>
              Customize exam…
            </Button>
          ) : null}
        </Box>
        {!plan2Only ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_, value) => {
              if (!value || !onExamModeChange) return;
              onExamModeChange(value);
            }}
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="exam">Exam</ToggleButton>
            <ToggleButton value="exam2">Exam 2</ToggleButton>
          </ToggleButtonGroup>
        ) : null}
        {mode === "exam" ? (
          <Typography variant="body1" color="text.secondary">
            Hide exam findings that do not apply. What remains goes into the note.
          </Typography>
        ) : null}
      </Box>

      {mode === "exam2" ? (
        <Exam2PlanStep
          systems={exam2Systems}
          examFindings={examFindings}
          diagnoses={diagnoses}
          loading={loading}
          error={error}
          retry={retry}
          onChangeSystemValue={onChangeExam2SystemValue}
          patientContext={patientContext}
          preferredBySystemId={preferredBySystemId}
          onMakeDefaultNormal={onMakeDefaultNormal}
          onCreateNormalExam={onCreateNormalExam}
          clinicalHintsOpen={clinicalHintsOpen}
        />
      ) : (
        <>
          {!loading && (
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
              <Button
                size="small"
                startIcon={<AddRounded />}
                onClick={() => setAddModalOpen(true)}
              >
                Add Examination
              </Button>
            </Box>
          )}

          {error ? (
            <DataLoadErrorAlert message={error} onRetry={retry} sx={{ width: "100%" }} />
          ) : null}

          {loading && !examFindings.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : null}

          {!loading && !error && !examFindings.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No exam findings for this differential. Add your own, or retry.
            </Typography>
          ) : null}

          {examFindings.map((finding) => (
            <SelectableCard
              key={finding.id}
              title={finding.name}
              rationale={finding.rationale}
              selectionVariant="none"
              disabled={disabledExamIds.has(finding.id)}
              onToggleDisabled={
                onToggleExamDisabled ? () => onToggleExamDisabled(finding.id) : undefined
              }
              hideTooltip="Exclude"
              restoreTooltip="Restore"
            />
          ))}

          <AddExamModal
            open={addModalOpen}
            onClose={() => setAddModalOpen(false)}
            onAdd={onAddExam}
            existingNames={existingNames}
          />
        </>
      )}

      <CustomizeExamModal
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        systems={exam2Systems}
        preferredBySystemId={preferredBySystemId}
        scopeLabel={scopeLabel}
        onSaveThisNote={onReplaceExam2Systems}
        onSaveDefaults={onSaveExam2LayoutDefaults}
      />
    </Box>
  );
};

export default ExamPlanStep;
